import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Mail, Lock, AlertTriangle, Eye, EyeOff, ArrowRight, ArrowLeft, Shield,
  DollarSign, Target, TrendingUp, Sparkles,
} from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import {
  ACCOUNT_SIZE_OPTIONS, RISK_OPTIONS, recommendInstrument, INSTRUMENT_SPECS,
} from '../utils/sizing';
import '../styles/pages/auth.css';

const SignUpPage = () => {
  const { signUp, isLoggedIn, loading: authLoading } = useAuthContext();
  const { setTradingProfile } = useAppContext();
  const navigate = useNavigate();

  // Form state
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '' });
  const [profile, setProfile] = useState({ accountSize: 10000, riskPercent: 1 });

  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && isLoggedIn && step === 1) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, isLoggedIn, navigate, step]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  // Step 1 — submit credentials, then move to profile step
  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Please fill in all fields');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await signUp(form.email, form.password);
      if (data?.user?.identities?.length === 0) {
        setError('An account with this email already exists');
        setLoading(false);
        return;
      }
      if (typeof window.fbq === 'function') window.fbq('track', 'CompleteRegistration');
      setStep(2);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — save profile and route to dashboard
  const handleProfileSubmit = (e) => {
    e.preventDefault();
    setTradingProfile({
      accountSize: profile.accountSize,
      riskPercent: profile.riskPercent,
      instrument: recommendInstrument(profile.accountSize),
    });
    navigate('/dashboard');
  };

  const skipProfile = () => navigate('/dashboard');

  const recommended = recommendInstrument(profile.accountSize);
  const spec = INSTRUMENT_SPECS[recommended];
  const riskBudget = profile.accountSize * (profile.riskPercent / 100);

  return (
    <div className="auth-page">
      <div className="auth-page__grid-bg" />
      <div className="auth-page__glow auth-page__glow--1" />
      <div className="auth-page__glow auth-page__glow--2" />

      <div className="auth-page__container">
        <Link to="/" className="auth-page__back">
          <ArrowLeft size={16} />
          Back to home
        </Link>

        <div className="auth-page__card">
          <div className="auth-page__header">
            <Link to="/" className="auth-page__logo">
              <img src="/logo.png" alt="AlphaNQ" className="auth-page__logo-img" />
            </Link>

            {/* Step indicator */}
            <div className="auth-page__steps">
              <div className={`auth-page__step ${step >= 1 ? 'auth-page__step--active' : ''} ${step > 1 ? 'auth-page__step--done' : ''}`}>
                <span className="auth-page__step-num">1</span>
                <span className="auth-page__step-label">Account</span>
              </div>
              <div className="auth-page__step-line" />
              <div className={`auth-page__step ${step >= 2 ? 'auth-page__step--active' : ''}`}>
                <span className="auth-page__step-num">2</span>
                <span className="auth-page__step-label">Your sizing</span>
              </div>
            </div>

            {step === 1 ? (
              <>
                <h1 className="auth-page__title">Create your account</h1>
                <p className="auth-page__desc">Get access to live Nasdaq futures signals and premium features</p>
              </>
            ) : (
              <>
                <h1 className="auth-page__title">Personalize your signals</h1>
                <p className="auth-page__desc">
                  Tell us your account size and we&apos;ll size every signal for you &mdash; no math, no guessing.
                </p>
              </>
            )}
          </div>

          {step === 1 && (
            <form className="auth-page__form" onSubmit={handleCredentialsSubmit}>
              {error && (
                <div className="auth-page__error">
                  <AlertTriangle size={15} />
                  <span>{error}</span>
                </div>
              )}

              <div className="auth-page__field">
                <label className="auth-page__label">Email address</label>
                <div className="auth-page__input-wrap">
                  <Mail size={16} className="auth-page__input-icon" />
                  <input
                    className="auth-page__input"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="you@example.com"
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="auth-page__field">
                <label className="auth-page__label">Password</label>
                <div className="auth-page__input-wrap">
                  <Lock size={16} className="auth-page__input-icon" />
                  <input
                    className="auth-page__input"
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Min. 6 characters"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button type="button" className="auth-page__toggle" onClick={() => setShowPw(!showPw)} tabIndex={-1}>
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div className="auth-page__field">
                <label className="auth-page__label">Confirm password</label>
                <div className="auth-page__input-wrap">
                  <Lock size={16} className="auth-page__input-icon" />
                  <input
                    className="auth-page__input"
                    type={showConfirmPw ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    placeholder="Re-enter your password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button type="button" className="auth-page__toggle" onClick={() => setShowConfirmPw(!showConfirmPw)} tabIndex={-1}>
                    {showConfirmPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <button type="submit" className="auth-page__submit" disabled={loading}>
                {loading ? (
                  <><span className="spinner" /> Creating account...</>
                ) : (
                  <>Continue <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}

          {step === 2 && (
            <form className="auth-page__form" onSubmit={handleProfileSubmit}>
              {/* Account size */}
              <div className="auth-page__field">
                <label className="auth-page__label">
                  <DollarSign size={13} className="auth-page__label-icon" />
                  Account size
                </label>
                <div className="auth-page__chip-grid">
                  {ACCOUNT_SIZE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`auth-page__chip ${profile.accountSize === opt.value ? 'auth-page__chip--active' : ''}`}
                      onClick={() => setProfile((p) => ({ ...p, accountSize: opt.value }))}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Risk per trade */}
              <div className="auth-page__field">
                <label className="auth-page__label">
                  <Target size={13} className="auth-page__label-icon" />
                  Risk per trade
                </label>
                <div className="auth-page__chip-row">
                  {RISK_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`auth-page__chip auth-page__chip--wide ${profile.riskPercent === opt.value ? 'auth-page__chip--active' : ''}`}
                      onClick={() => setProfile((p) => ({ ...p, riskPercent: opt.value }))}
                    >
                      <span className="auth-page__chip-main">{opt.label}</span>
                      <span className="auth-page__chip-sub">{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Live preview */}
              <div className="auth-page__preview">
                <div className="auth-page__preview-head">
                  <Sparkles size={13} />
                  Your personalized sizing
                </div>
                <div className="auth-page__preview-rows">
                  <div className="auth-page__preview-row">
                    <span className="auth-page__preview-label">Recommended contract</span>
                    <span className="auth-page__preview-val auth-page__preview-val--accent">
                      <TrendingUp size={12} />
                      {spec.symbol} <span className="auth-page__preview-sub">&middot; {spec.name}</span>
                    </span>
                  </div>
                  <div className="auth-page__preview-row">
                    <span className="auth-page__preview-label">Risk budget per trade</span>
                    <span className="auth-page__preview-val">${riskBudget.toLocaleString()}</span>
                  </div>
                  <div className="auth-page__preview-row">
                    <span className="auth-page__preview-label">Position sizing</span>
                    <span className="auth-page__preview-val">Auto-calculated on every signal</span>
                  </div>
                </div>
              </div>

              <button type="submit" className="auth-page__submit">
                Finish setup <ArrowRight size={16} />
              </button>
              <button type="button" className="auth-page__skip" onClick={skipProfile}>
                Skip for now
              </button>
            </form>
          )}

          <div className="auth-page__secure">
            <Shield size={13} />
            Your information is encrypted and secure
          </div>

          {step === 1 && (
            <div className="auth-page__footer">
              Already have an account?{' '}
              <Link to="/login" className="auth-page__link">Sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
